import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { Service, SortOrderDirection } from 'webapp-types';
import { OrderDirection } from 'webapp/enums/enums';
import { uniq } from 'webapp/utils/common';
import type { SearchProps } from 'webapp/utils/filter';
import { isVerified } from 'webapp/utils/verification';

import type Board from 'webapp/models-custom/board';
import type { FilterSortSettings } from 'webapp/models-custom/filter-sort/base';
import FilterSort from 'webapp/models-custom/filter-sort/base';
import { RECENTLY_DELETED_ID, type FilterOption } from 'webapp/models-custom/filter-sort/filter';

export enum BoardCategory {
	ALL = 0,
	FAVORITE = 5,
	WITH_ISSUES = 7,
	IN_ERROR = 8,
	VERIFIED = 9,
	DELETED = RECENTLY_DELETED_ID,
}

export enum BoardFilterId {
	CATEGORY = 1,
	TAGS = 2,
	DATA_SOURCE = 3,
	CREATOR = 4,
	OWNER = 5,
}

export const settingsNamePrefix = 'boardsFilterSettings'; // used to save filter preferences

export default class FilterSortBoard extends FilterSort<Board> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('model-manager/data-performance-summaries')
	declare mmDataPerformanceSummariesService: Service.ModelManager.DataPerformanceSummaries;
	@service('flow/promotions') declare flowPromotionsService: Service.Flow.Promotions;

	@tracked override sortProp = 'order';
	@tracked override sortOrder: SortOrderDirection = OrderDirection.ASC;

	override searchProps: SearchProps = {
		name: 1,
	};

	override settingsNamePrefix = settingsNamePrefix;
	override trackingObjectName = 'board';

	constructor(options: FilterSortSettings<Board>) {
		super(options);

		const selectedFilters = this.readSavedSelectedFilters();

		// Data source (used only)
		this.createFilter<string>({
			id: BoardFilterId.DATA_SOURCE,
			label: 'Data source',
			name: 'data source',
			selected: selectedFilters[BoardFilterId.DATA_SOURCE] as string[] | undefined,
			stringValues: true,
			isMultiSelect: true,
			getOptions: () => {
				return this.datasourceOptions;
			},
			filterFn: (item, selected) => {
				// handle multi-select: if selected is an array, check if any match
				const selectedArray = Array.isArray(selected) ? selected : [selected];

				if (selectedArray.includes('all')) {
					return true;
				}

				return selectedArray.some((selectedValue) => {
					const sourceTypeId =
						this.mmSourceTypesService.findModelByKey(selectedValue)?.id;
					return (
						item.specificSourceTypesKeys.includes(selectedValue) ||
						(sourceTypeId &&
							item.sourceTypeIdsMissingFromResolutionMessages.includes(sourceTypeId))
					);
				});
			},
		});

		// Owner
		this.createFilter<number>({
			id: BoardFilterId.OWNER,
			label: 'Owned by',
			name: 'owner',
			selected: selectedFilters[BoardFilterId.OWNER] as number[] | undefined,
			isMultiSelect: true,
			getOptions: () => {
				return this.ownerOptions;
			},
			filterFn: function (item, selected) {
				if (selected.includes(-1)) {
					// exception: all others
					return !item.isOwnedByMe;
				} else if (selected.includes(0)) {
					// all option
					return true;
				} else {
					return selected.some((ownerId) => item.ownerId === ownerId);
				}
			},
		});

		// Creator
		this.createFilter<number>({
			id: BoardFilterId.CREATOR,
			label: 'Created by',
			name: 'creator',
			selected: selectedFilters[BoardFilterId.CREATOR] as number[] | undefined,
			isMultiSelect: true,
			getOptions: () => {
				return this.creatorOptions;
			},
			filterFn: function (item, selected) {
				if (selected.includes(-1)) {
					// exception: all others
					return !item.isMine;
				} else if (selected.includes(0)) {
					// all option
					return true;
				} else {
					return selected.includes(item.createdById);
				}
			},
		});

		// Category
		this.createFilter<number>({
			id: BoardFilterId.CATEGORY,
			label: 'Show',
			name: 'category',
			selected: selectedFilters[BoardFilterId.CATEGORY] as number[] | undefined,
			isMultiSelect: true,
			getOptions: () => {
				return this.categoryOptions;
			},
			onSelectedChange: (filter) => {
				this.flowPromotionsService.endRecentlyDeletedTour();

				if (filter.selected.includes(BoardCategory.DELETED)) {
					void this.mmBoardsService.requestDeletedBoards();
				}
			},
			filterFn: function (item, selected) {
				return selected.some((category) => {
					switch (category) {
						case BoardCategory.ALL:
							return !item.deleted;
						case BoardCategory.FAVORITE:
							return item.favorite;
						case BoardCategory.WITH_ISSUES:
							return item.isAnythingToSolve;
						case BoardCategory.IN_ERROR:
							return !!item.sourcesInError.length;
						case BoardCategory.VERIFIED:
							return isVerified(item.verificationInfo);
						case BoardCategory.DELETED:
							return !!item.deleted && item.canCurrentUserEditWithoutDeleted;
						default:
							return false;
					}
				});
			},
		});

		// to apply "Recently deleted" filter after page refresh when filter is set
		if (this.findFilter(BoardFilterId.CATEGORY)?.selected.includes(BoardCategory.DELETED)) {
			void this.mmBoardsService.requestDeletedBoards();
		}

		// Tags (used only)
		this.createFilter<number>({
			id: BoardFilterId.TAGS,
			label: 'Tags',
			name: 'tag',
			selected: selectedFilters[BoardFilterId.TAGS] as number[] | undefined,
			isMultiSelect: true,
			getOptions: () => {
				return this.tagOptions;
			},
			filterFn: function (item, selected) {
				if (selected.includes(0)) {
					// all tags
					return true;
				} else {
					return selected.some((tagId) => item.tags.includes(tagId));
				}
			},
		});
	}

	override get hideViewSelector(): boolean {
		// to prevent hiding view selectors if any of view have zero items (especially for transfered account or public templates)
		return false;
	}

	override get viewOptions(): FilterOption<string>[] {
		return [
			{ id: 'table', text: 'List view', icon: 'List' },
			{ id: 'grid', text: 'Grid view', icon: 'Grid' },
			{ id: 'flow', text: 'Desktop view', icon: 'Loop' },
		];
	}

	get categoryOptions(): FilterOption<number>[] {
		// id of filters shouldn't be changed (only add or remove) because they are saved in usres local storage
		// id [1,2,3,4,6] have already been used, but later removed so they shouldn't be used again
		const categoryOptionsBase: FilterOption<number>[] = [
			{ id: BoardCategory.ALL, text: 'All' },
			{
				id: BoardCategory.FAVORITE,
				text: 'Favorites',
				icon: 'StarFill',
				color: 'var(--databox-yellow-500)',
			},
		];

		if (
			this.mmBoardsService.normalBoardModels.filter((item) => item.isAnythingToSolve).length
		) {
			categoryOptionsBase.push({
				id: BoardCategory.WITH_ISSUES,
				text: 'With issues',
				icon: 'AlertFill',
				color: 'var(--databox-orange-500)',
			});
		}

		if (this.mmBoardsService.normalBoardModels.filter((item) => item.sourcesInError).length) {
			categoryOptionsBase.push({
				id: BoardCategory.IN_ERROR,
				text: 'In error',
				icon: 'AlertFill',
				color: 'var(--databox-red-500)',
			});
		}

		categoryOptionsBase.push({
			id: BoardCategory.VERIFIED,
			text: 'Verified',
			icon: 'VerifiedFill',
		});

		// if on flow view, viewId is empty string and recently deleted should not be shown
		if (this.viewId && !this.currentUserService.isViewer) {
			categoryOptionsBase.push({
				id: BoardCategory.DELETED,
				text: 'Recently deleted',
				icon: 'Trash',
				class: 'top-divider',
				notCombinable: true,
			});
		}

		return categoryOptionsBase;
	}

	get tagOptions(): FilterOption<number>[] {
		const values = this.mmBoardsService.assignedTagsForBoards,
			arrayCopy = Array.from(values) as FilterOption<number>[];
		if (arrayCopy.length) {
			arrayCopy.unshift({ id: 0, text: 'All' });
		}
		return arrayCopy;
	}

	@cached
	get datasourceOptions(): FilterOption<string>[] {
		// get how many boards each source is used on
		const sourceTypeArray: string[] = [];

		this.allItems.forEach((board) => {
			const sourcesFromBoard: string[] = [];
			board.specificSourceTypesKeys.forEach((key: string) => {
				if (key) {
					sourcesFromBoard.push(key);
				}
			});
			board.sourceTypeIdsMissingFromResolutionMessages.forEach((missingType: number) => {
				if (missingType) {
					const missingSource = this.mmSourceTypesService.findModelById(missingType);

					if (missingSource) {
						sourcesFromBoard.push(missingSource.key);
					}
				}
			});
			uniq(sourcesFromBoard).forEach((key: string) => {
				sourceTypeArray.push(key);
			});
		});

		const list = this.mmSourceTypesService.getSourceTypesListWithCount(sourceTypeArray);

		list.unshift({ id: 'all', text: 'All' });

		return list;
	}

	@cached
	get creatorOptions(): FilterOption<number>[] {
		const allItemCountByCreator: Record<number, number> = {};
		this.allItems.forEach((board: Board) => {
			const createdById = board.createdById;
			if (!allItemCountByCreator[createdById]) {
				allItemCountByCreator[createdById] = 0;
			}
			allItemCountByCreator[createdById]++;
		});

		return this.mmUsersService.getFilterOptionsForUsers(allItemCountByCreator);
	}

	get ownerOptions(): FilterOption<number>[] {
		const allItemCountByOwner: Record<number, number> = {};
		this.allItems.forEach((item: Board) => {
			const ownerId = item.ownerId!; // without an owner, counted under the "null" / "undefined" key
			if (!allItemCountByOwner[ownerId]) {
				allItemCountByOwner[ownerId] = 0;
			}
			allItemCountByOwner[ownerId]++;
		});

		return this.mmUsersService.getFilterOptionsForUsers(allItemCountByOwner);
	}
}
